import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote, MapPin } from 'lucide-react';

const testimonials = [
  {
    name: 'Priya Sharma',
    role: 'Founder, TextilePro Pvt Ltd',
    location: 'Surat, Gujarat',
    avatar: 'PS',
    color: 'from-pink-500 to-rose-500',
    rating: 5,
    quote: 'MSMECenter helped us register our company, get GSTIN, and secure a ₹15 lakh loan – all within 2 weeks! The team is incredibly responsive and professional.',
  },
  {
    name: 'Rahul Verma',
    role: 'CEO, TechStart Solutions',
    location: 'Bangalore, Karnataka',
    avatar: 'RV',
    color: 'from-blue-500 to-cyan-500',
    rating: 5,
    quote: 'The AI-powered dashboard gives me real-time compliance alerts. I never miss a GST deadline anymore. Best investment for my startup.',
  },
  {
    name: 'Sunita Patel',
    role: 'Director, Patel Foods Ltd',
    location: 'Ahmedabad, Gujarat',
    avatar: 'SP',
    color: 'from-amber-500 to-orange-500',
    rating: 5,
    quote: 'From trademark registration to monthly GST filings – MSMECenter handles everything. It saved us ₹3 lakhs annually compared to hiring an in-house CA.',
  },
  {
    name: 'Amit Kumar',
    role: 'Owner, Kumar Manufacturing',
    location: 'Ludhiana, Punjab',
    avatar: 'AK',
    color: 'from-green-500 to-emerald-500',
    rating: 5,
    quote: 'I was skeptical at first, but the loan pre-approval in 48 hours changed everything. Used the funds to buy new machinery and doubled our production capacity.',
  },
  {
    name: 'Deepa Nair',
    role: 'Founder, Nair Consultants',
    location: 'Kochi, Kerala',
    avatar: 'DN',
    color: 'from-purple-500 to-violet-500',
    rating: 5,
    quote: 'The digital marketing package transformed our online presence. Website traffic increased 400% and we now get 20+ quality leads per month.',
  },
];

export function Testimonials() {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent(i => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent(i => (i + 1) % testimonials.length);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);

  const t = testimonials[current];

  return (
    <section className="py-12 md:py-24 bg-white dark:bg-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-purple-600/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400 text-sm font-bold mb-6">
            <Star className="w-4 h-4" /> Client Stories
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white font-heading">What Our Clients Say</h2>
          <p className="text-slate-900 dark:text-slate-400 mt-4 text-lg">Real results from real Indian businesses.</p>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.4 }}
              className="backdrop-blur-xl bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none rounded-3xl p-8 md:p-12 shadow-2xl"
            >
              <Quote className="w-12 h-12 text-purple-400/40 mb-6" />
              <p className="text-slate-900 dark:text-slate-100 text-xl md:text-2xl font-medium leading-relaxed mb-8 italic">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${t.color} flex items-center justify-center text-slate-900 dark:text-white font-black text-lg shadow-lg`}>
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-slate-900 dark:text-white font-bold text-lg">{t.name}</div>
                    <div className="text-slate-900 dark:text-slate-300 text-sm">{t.role}</div>
                    <div className="text-slate-900 dark:text-slate-400 text-xs mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-600 dark:text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-1">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  aria-label={`Go to testimonial ${i + 1} of ${testimonials.length}`}
                  aria-current={i === current ? 'true' : undefined}
                  className="p-2.5 flex items-center justify-center"
                >
                  <span
                    className={`block rounded-full transition-all duration-300 ${
                      i === current ? 'w-8 h-2 bg-cyan-400' : 'w-2 h-2 bg-gray-300 dark:bg-white/20 hover:bg-white/40'
                    }`}
                  />
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={prev}
                aria-label="Previous testimonial"
                className="w-12 h-12 rounded-full bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none text-slate-900 dark:text-white hover:bg-gray-200 dark:bg-[#111C2F] hover:border-gray-300 dark:border-[#2A3F5F] flex items-center justify-center transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next testimonial"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:shadow-[0_0_25px_rgba(6,182,212,0.6)] transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
